//================================================================
//  Component: AddAllocationItemPane
//================================================================

//  Purpose: This pane will allow users to add an allocation item

//  Properties:
//    - task = {An object, contains the commercial task details}
//    - addAllocationItemPaneOpen = {useState, used to determine if the pane is visible}
//    - setAddAllocationItemPaneOpen = {useState, used to toggle the visibility of the allocation pane}

//  Example:
//    <AddAllocationItemPane
//      task={task}
//      addAllocationItemPaneOpen={addAllocationItemPaneOpen}
//      setAddAllocationItemPaneOpen={setAddAllocationItemPaneOpen}
//    ></AddAllocationItemPane>    

//================================================================


//Libraries
import React, { useState, useContext } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../../../Library/GlobalContexts';

//Components
import PTELookupRHF from '../../../../../Components/PTELookupRHF/PTELookupRHF';

//Functions
import AddRemoveDocumentArray from '../../../../../Library/AddRemoveDocumentArray';

// Images
import Error from '../../../../../Components/Images/Icon_ErrorFatal_Red.svg';
import Loading from '../../../../../Components/Images/Image_Loading_Ripple.svg';


export default function AddAllocationItemPane({
    task,
    addAllocationItemPaneOpen,
    setAddAllocationItemPaneOpen
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // useState for pane status
    const [paneStatus, setPaneStatus] = useState('onload');

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    const { register, handleSubmit, formState: { errors }, reset, clearErrors, setValue, watch } = useForm();

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submit Handler
    function onSubmit(data) {

        setPaneStatus('pending');

        const updatedDocument = {
            projectcode: data.projectcode,
            tasknumber: data.tasknumber,
            expendituretype: data.expendituretype,
            allocationpercentage: parseFloat(data.allocationPercentage),
            allocationamount: parseFloat(data.allocationAmount),
            businessunit: data.businessUnit
        };

        const modifiedBy = {
            'email': getUser.emailaddress,
            'givenname': getUser.givenname,
            'surname': getUser.surname,
        };

        AddRemoveDocumentArray('requests', task.requestid, 'demandassessment.allocations', updatedDocument, 'add', modifiedBy)
            .then(() => {
                reset();
                clearErrors();
                setPaneStatus('onload');
                setAddAllocationItemPaneOpen(false);

            })
            .catch((error) => {
                console.log('error', error);
                setPaneStatus('error');

            });

    }

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    // Hide Pane 
    if (!addAllocationItemPaneOpen) return null;

    // Onload State
    if (paneStatus === 'onload') {

        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container'>
                    <form className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div className='flex flex-col gap-2 p-4'>
                            <h4 className='text-[22px] p-0 m-0'> Add Cost Allocation</h4>
                        </div>

                        {/* Body */}
                        <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                            {/* Recharge Information */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>Recharge Information <span className='text-[#C4314B]'>*</span></label>

                                <i className='mb-3'>Project code, task number and expenditure type allow the recharge of technology costs.</i>

                                <PTELookupRHF
                                    required={true}
                                    register={register}
                                    setValue={setValue}
                                    errors={errors}
                                    clearErrors={clearErrors}
                                    disabled={false}
                                    watch={watch}
                                    defaultValue={{
                                        'projectcode': task?.projectcode,
                                        'tasknumber': task?.tasknumber,
                                        'expendituretype': task?.expendituretype
                                    }}
                                ></PTELookupRHF>

                            </div>

                            {/* Allocation Percentage */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>Allocation (%) <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.allocationPercentage ? 'Input-Field-Text-Error' : 'Input-Field-Text'}
                                    style={{ width: '300px' }}
                                    type='number'
                                    step='0.01'
                                    {...register('allocationPercentage', {
                                        required: 'This field is required',
                                        pattern: {
                                            value: /^\d+(\.\d{1,2})?$/,
                                            message: 'Only numbers allowed (up to 2 decimal places)',
                                        },
                                        min: {
                                            value: 0,
                                            message: 'Value must be at least 0',
                                        },
                                        max: {
                                            value: 100,
                                            message: 'Value must not exceed 100',
                                        },
                                    })}
                                    placeholder='10%'
                                ></input>
                                {errors.allocationPercentage && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.allocationPercentage?.message}</p>}
                            </div>

                            {/* Allocation Amount */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>Allocation Amount ($) <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.allocationAmount ? 'Input-Field-Text-Error' : 'Input-Field-Text'}
                                    style={{ width: '300px' }}
                                    type='number'
                                    step='0.01'
                                    {...register('allocationAmount', {
                                        required: 'This field is required',
                                        pattern : {
                                            value: /^\d+(\.\d{1,2})?$/,
                                            message: 'Only numbers allowed (up to 2 decimal places)',
                                        },
                                        min: {
                                            value: 0,
                                            message: 'Amount must be greater than 0'
                                        }
                                    })}
                                    placeholder='100,000'
                                ></input>
                                {errors.allocationAmount && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.allocationAmount?.message}</p>}
                            </div>

                            {/* Business Unit */}
                            <div className='FormComponent-Row mb-[2%]'>
                                <label className='font-medium'>
                                    Business Unit <span className='text-[#C4314B]'>*</span>
                                </label>
                                <select
                                    className={errors.businessUnit ? 'Input-Field-Select-Error' : 'Input-Field-Select'}
                                    style={{ width: '300px' }}
                                    {...register('businessUnit', {
                                        required: 'This field is required'
                                    })}
                                    placeholder='Select business unit'
                                >
                                    <option hidden value=''>Select Business Unit</option>
                                    <option value='Investment Management'>Investment Management</option>
                                    <option value='Investments'>Investments</option>
                                    <option value='Development'>Development</option>
                                    <option value='Construction'>Construction</option>
                                    <option value='Defence'>Defence</option>
                                    <option value='Corporate Affairs & Marketing'>Corporate Affairs & Marketing</option>
                                    <option value='Corporate Real Estate'>Corporate Real Estate</option>
                                    <option value='Digital'>Digital</option>
                                    <option value='EH&S'>EH&S</option>
                                    <option value='Finance'>Finance</option>
                                    <option value='Legal'>Legal</option>
                                    <option value='People & Culture'>People & Culture</option>
                                    <option value='Risk & Insurance'>Risk & Insurance</option>
                                    <option value='Sustainability'>Sustainability</option>
                                    <option value='Tax'>Tax</option>
                                    <option value='Treasury'>Treasury</option>
                                </select>
                                {errors.businessUnit && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.businessUnit?.message}</p>}
                            </div>

                        </div>

                        {/* Footer */}
                        <div className='px-4 py-3 flex gap-3'>

                            {/* Add Button */}
                            <button className='Primary-Button' type='submit' value='submit' disabled={Object.keys(errors).length > 0}>
                                Add
                            </button>

                            {/* Cancel */}
                            <button
                                className='Secondary-Button'
                                onClick={() => {
                                    reset();
                                    clearErrors();
                                    setAddAllocationItemPaneOpen(false);
                                }}
                            >
                                Cancel
                            </button>

                        </div>
                    </form>
                </dialog>
            </div>
        );
    }

    // Pending State
    if (paneStatus === 'pending') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container flex justify-center items-center text-center'>
                    <img alt='loading-circle-icon' src={Loading} />
                </dialog>
            </div>
        );
    }

    // Error State
    if (paneStatus === 'error') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container'>
                    <div className='flex flex-col gap-2 w-full justify-center items-center'>
                        <img className='my-2 w-[100px]' src={Error} alt='Error' />
                        <h4 className='mb-0'>Oops! Something went wrong.</h4>
                        <p className='text-center leading-[1.7]'>
                            An error occurred while we processed your request.
                        </p>
                        <button
                            className='Primary-Button'
                            onClick={() => {
                                reset();
                                clearErrors();
                                setPaneStatus('onload');
                                setAddAllocationItemPaneOpen(false);
                            }}
                        >
                            Close
                        </button>
                    </div>
                </dialog>
            </div>
        );
    }
}